import * as React from 'react'

import { inject, observer } from 'mobx-react'

import { Button } from '~/client/src/shared/components/Button/Button'
import { useCompanyListSelectorData } from '~/client/src/shared/components/CompanyListSelector'
import { AddIcon } from '~/client/src/shared/components/icons/AddIcon'
import { Separator } from '~/client/src/shared/components/shadcn-ui/separator'
import Spinner from '~/client/src/shared/components/Spinner/Spinner'
import { useUserListSelectorData } from '~/client/src/shared/components/UserListSelector'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import OperationRulesStore from '~/client/src/shared/stores/domain/OperationRules.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import ProjectRolesStore from '~/client/src/shared/stores/domain/ProjectRoles.store'
import ProjectTeamsStore from '~/client/src/shared/stores/domain/ProjectTeams.store'
import ProjectTradesStore from '~/client/src/shared/stores/domain/ProjectTrades.store'
import TagsStore from '~/client/src/shared/stores/domain/Tags.store'
import UserProjectsStore from '~/client/src/shared/stores/domain/UserProjects.store'
import EventsStore from '~/client/src/shared/stores/EventStore/Events.store'
import InitialState from '~/client/src/shared/stores/InitialState'

import SignUpRuleRow from './SignUpRuleRow'
import { useSignUpNotificationRules } from './useSignUpNotificationRules'

interface IProps {
  operationRulesStore?: OperationRulesStore
  companiesStore?: CompaniesStore
  eventsStore?: EventsStore
  projectMembersStore?: ProjectMembersStore
  userProjectsStore?: UserProjectsStore
  state?: InitialState
  projectRolesStore?: ProjectRolesStore
  projectTeamsStore?: ProjectTeamsStore
  projectTradesStore?: ProjectTradesStore
  tagsStore?: TagsStore
}

const SignUpNotificationRulesSection: React.FC<IProps> = observer(
  ({
    operationRulesStore,
    companiesStore,
    eventsStore,
    projectMembersStore,
    userProjectsStore,
    state,
    projectRolesStore,
    projectTeamsStore,
    projectTradesStore,
    tagsStore,
  }) => {
    const signUpRules = useSignUpNotificationRules({
      eventsStore,
      operationRulesStore,
      projectMembersStore,
    })
    const { rules, isSaving } = signUpRules
    const {
      users: selectorUsers,
      userMetadata,
      currentUser,
      filterData,
    } = useUserListSelectorData({
      projectMembersStore,
      userProjectsStore,
      companiesStore,
      state,
      projectRolesStore,
      projectTeamsStore,
      projectTradesStore,
      tagsStore,
    })
    const { companies: selectorCompanies, companyMetadata } =
      useCompanyListSelectorData({ companiesStore, projectTradesStore })

    return (
      <div className="tw:flex tw:flex-col tw:gap-4 tw:w-full tw:relative tw:min-h-[40px]">
        {isSaving && (
          <div className="tw:absolute tw:inset-0 tw:bg-white/60 tw:z-10 tw:flex tw:items-center tw:justify-center tw:rounded-lg">
            <Spinner size="sm" />
          </div>
        )}
        {rules.map((rule, index) => (
          <React.Fragment key={rule.id}>
            {index > 0 && <Separator className="tw:my-0" />}
            <SignUpRuleRow
              rule={rule}
              ruleNumber={index + 1}
              canDelete={rules.length > 1}
              store={signUpRules}
              companiesStore={companiesStore}
              selectorUsers={selectorUsers}
              userMetadata={userMetadata}
              currentUser={currentUser}
              filterData={filterData}
              selectorCompanies={selectorCompanies}
              companyMetadata={companyMetadata}
            />
          </React.Fragment>
        ))}

        {rules.length > 0 && <Separator className="tw:my-0" />}

        <div className="tw:flex tw:items-center tw:justify-between tw:w-full">
          <Button
            variant="outline"
            size="lg"
            onClick={signUpRules.createNewRule}
          >
            <AddIcon data-icon="inline-start" size="xs" />
            {Localization.translator.newRule}
          </Button>
          <Button
            variant="default"
            size="lg"
            onClick={signUpRules.save}
            disabled={!signUpRules.isDirty || signUpRules.isSaving}
          >
            {signUpRules.isSaving && (
              <span data-icon="inline-start" className="tw:inline-flex">
                <Spinner size="sm" variant="white" />
              </span>
            )}
            {Localization.translator.save}
          </Button>
        </div>
      </div>
    )
  },
)

export default inject(
  'operationRulesStore',
  'companiesStore',
  'eventsStore',
  'projectMembersStore',
  'userProjectsStore',
  'state',
  'projectRolesStore',
  'projectTeamsStore',
  'projectTradesStore',
  'tagsStore',
)(SignUpNotificationRulesSection)
